import { Logger, LoggerService } from '@Shared/logger';
import { PaywayRepository } from '../repository';
import { UIUtil } from '@Shared/utils/ui';
import {
  ApplePayRecurringPaymentRequestBody,
  PaymentLineItems,
  PAYWAY_UI_CONST,
  PAYWAYSERVICES,
  QueuePaywayTokenRequest,
  QueuePaywayTokenResponse,
  PAYWAY_MERCHANT_ID,
  PaywayBillingRequest,
  NewSubscriptionStartBody,
  OfferDetails
} from '../_models';
import { SETTINGS } from '@Config';
import { HttpService } from '@Shared/http';
import { DomainUtil } from '@Shared/utils/data';
import { MediaGroup, OptionsModule } from '@Modules/options_module';
import { Auth0Service } from '@Modules/auth_module';
import { PipelineModule } from '@Modules/index';
import { ClearEntitlementCacheStep } from '@Modules/pipeline_module/steps/entitlement/clear-entitlement-cache.step'
import { ScreenManager, UI_CONST } from '@Modules/ui_module';
import { STORAGE } from '../../../core/models';
import { ProcessNewStartStep } from '@Modules/pipeline_module/steps/payway/process-new-start.step';
const { hideElement, showElement, doesElementExist, getDataAttributeAsInt } = UIUtil;
const { applePayButton: applePayButtonId, appleRow: applePayRowId, paywayTerms, appleButtons: appleButtonsId } = PAYWAY_UI_CONST;

export class PaywayService extends PaywayRepository {
  private Logger: Logger = LoggerService.createLogger('PaywayService');
  private static instance: PaywayService;
	protected appleRow: HTMLElement | null = null;
  protected appleButton: HTMLElement | null = null;
  private serviceURL: string = SETTINGS.environment === 'Prod' ? PAYWAYSERVICES.serviceUrlProd : PAYWAYSERVICES.serviceUrlDev;
  private billingURL: string = SETTINGS.environment === 'Prod' ? PAYWAYSERVICES.billingUrlProd : PAYWAYSERVICES.billingUrlDev;
  
  static get Instance() {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new PaywayService();
    }
    return this.instance;
  }

  constructor() {
    super();
    this.Logger.trace('****  CTOR ****');
  }

  public init(): Promise<void> {
    this.Logger.trace('initializing Payway Service...');

    try {
      this.registerUIEvents();
    } catch (error) {
      this.Logger.error('Error initializing Payway Service', error);
    } finally {
      return Promise.resolve();
    }
  };

  private registerUIEvents(): void {
    UIUtil.registerDynamicClickEvent<HTMLButtonElement>(
      UI_CONST.paymentCancelAction,
      (event, element) => {
        this.Logger.trace('Payment Cancel Button clicked');
        ScreenManager.Instance.closeScreen();
        this.localStorageManger.delete(STORAGE.applePayBillingRequestKey);
        this.localStorageManger.delete(STORAGE.applePayNewStartKey);
      },
    );

    UIUtil.registerDynamicClickEvent<HTMLButtonElement>(
      UI_CONST.paymentContinueAction,
      async (event, element) => {
        this.Logger.trace('Payment Continue Button clicked');
        //this.processNewSubscriptionStart();
        await PipelineModule.startUserInitiatedAuthPipeline(
          new ProcessNewStartStep(),
        );
      },
    );

    UIUtil.registerDynamicClickEvent<HTMLButtonElement>(
      PAYWAY_UI_CONST.appleButton,
      async (event, element) => {
        this.Logger.trace('APPLE PAY | Payment Button clicked:', element.textContent);
        await this.handleUseApplePayButtonClick();
      },
    );

    UIUtil.registerDynamicClickEvent<HTMLButtonElement>(
      PAYWAY_UI_CONST.applePaySubscribeButton,
      (event, element) => {
        this.Logger.trace('Apple Pay Subscribe Button clicked:');
      },
    );

    UIUtil.registerDynamicClickEvent<HTMLButtonElement>(
      applePayButtonId,
      async (event, element) => {
        this.Logger.trace('APPLE PAY | Payment Button clicked:');
        await this.handleAppleSubscribeButtonClick();
      },
    );

    document.addEventListener('onPaywallShown', async (e) => {
      this.Logger.trace('Paywall shown event received:', e);
      this.checkApplePayAvailability();
    }, { once: true });
  }

  private async checkApplePayAvailability(): Promise<void> {
    this.Logger.trace( 'APPLE PAY | Checking availability...' );
    
    if ( !doesElementExist( applePayButtonId ) ) {
      this.Logger.trace('APPLE PAY | No Apple Pay button on page, exit');
      return;
    }
    // Handles a current a/b test paywall with multiple button clicks and hides both buttons.
    // They SHOULD be hidden by default but this has sometimes been overlooked when paywalls are created. so this is a guard
    // Apple is adament that the button not be shown unless Apple Pay is available AND ready
    if ( doesElementExist( applePayRowId ) ) {
        this.twoStepPaywall = true
        hideElement( applePayRowId );
        hideElement( applePayButtonId );
    }
    //handle hide for *current paywall*
    if ( !this.twoStepPaywall ) {
      hideElement( applePayButtonId);
    }
    this.offerId = getDataAttributeAsInt( applePayButtonId, 'offercode' );
    this.offerGroupId = getDataAttributeAsInt( applePayButtonId, 'offergroup' );

    if(!this.offerGroupId || !this.offerId) {
      this.Logger.trace('APPLE PAY | Missing offer IDs, exit ');
      return;
    }
    //After guard check Apple Pay availability
    if ( typeof ApplePaySession !== 'undefined' && ApplePaySession.canMakePayments() ) {
      //For payway's flow we need to queue *immediately*
      this.queuePaywaySession();
    } else {
      this.Logger.trace('Apple Pay is not available');
    }
  }

  private async queuePaywaySession(): Promise<void> {
    this.Logger.trace('APPLE PAY | Queueing Payway session for env ', SETTINGS.environment);
    
    if(window.location.hostname === 'staging.republicanherald.com') {
      this.offerId = 4638;
      this.offerGroupId = 1852;
      this.offerDetails = this.getBillingDetails(1852, 4638);
    } else {
      this.offerDetails = await this.resolveOfferDetails( applePayButtonId, this.terms);
    }
    
    if (!this.offerDetails) {
      this.Logger.error('APPLE PAY | Failed to get offer details');
      return;
    }
    this.Logger.trace('APPLE PAY | Offer details:', this.offerDetails);
    const queueAmount = this.offerDetails?.promo.amount.replace('.', '');
    this.sourceId = this.getSourceId();

    const queuedPaywayTokenBody: QueuePaywayTokenRequest = {
      amount: queueAmount,
      eciType: 1,
      sourceId: this.sourceId,
      tax: '0',
      transactionNotes1: '',
      transactionNotes2: '',
      transactionNotes3: '',
      request: 'queueAuthorize'
    };
    try {
      const httpService = new HttpService(SETTINGS.entitler.baseUrl, 0);
      const response = await httpService.post2<QueuePaywayTokenResponse>(
        'payway/queue_transaction',
        queuedPaywayTokenBody,
        SETTINGS.entitler.apiKey,
      );
      if (!response.success) {
        this.Logger.error('APPLE PAY | Failed to queue Payway session: ', response);
        return;
      }
      this.queuePaywayTokenResponse = response.data ?? null;
      const data = response.data;
      this.Logger.trace('APPLE PAY | Queued Payway token response data:', data);
      if(this.twoStepPaywall) {
        showElement( applePayRowId );
      } else {
        showElement( applePayButtonId );
      }
    } catch (error) {
      this.Logger.error('Error queueing Payway session:', error);
    }
  }

  private async handleUseApplePayButtonClick(): Promise<void> {
    this.Logger.trace('APPLE PAY | Handling Use Apple Button click');
  
    hideElement(applePayRowId);
    showElement(paywayTerms);
    showElement(applePayButtonId);
  }

  private async handleAppleSubscribeButtonClick(): Promise<void> {
    this.Logger.trace('Handling Apple Subscribe Button click');
    if(!this.offerDetails) {
      return;
    }
    window.dataLayer?.push({
      event: 'apple-pay-button-clicked',
      userAction: 'click',
      uiSection: 'paywall',
      uiElementType: 'button',
    });

    //remove this from shared and move to new payway option param.
    const mediaGroup: MediaGroup = DomainUtil.isTribune()
      ? 'TribuneCEP'
      : 'MNG';

    const paywayRequestToken = this.queuePaywayTokenResponse!.paywayRequestToken;

    const lineItemArray: PaymentLineItems = [];
    if (this.offerDetails?.promo) {
      lineItemArray.push(this.offerDetails.promo);
    }
    lineItemArray.push(this.offerDetails.regularBilling);

    const paymentRequest: ApplePayRecurringPaymentRequestBody = {
      requiredBillingContactFields: ['postalAddress', 'phone'],
      countryCode: 'US',
      currencyCode: 'USD',
      merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      lineItems: lineItemArray as ApplePayJS.ApplePayLineItem[],
      recurringPaymentRequest: {
        paymentDescription: this.offerDetails.paymentDescription,
        regularBilling: this.offerDetails.regularBilling,
        trialBilling: this.offerDetails.promo,
        billingAgreement: this.offerDetails.billingAgreement,
        managementURL: `https://myaccount.${OptionsModule.Options.domain}`,
        tokenNotificationURL: `https://aws-mng-prod.subscriberconcierge.com/Webhooks/Apple/V2/${mediaGroup}/${OptionsModule.Options.siteCode}/${OptionsModule.Options.siteCode}`,
      },
      total: {
        label: this.offerDetails.promo.label,
        amount: this.offerDetails.promo.amount,
      },
    };

    await this.initApplePaySession(paymentRequest, paywayRequestToken);
  }

  private async initApplePaySession( paymentRequest: ApplePayRecurringPaymentRequestBody, paywayRequestToken: string ): Promise<void> {
    this.Logger.trace('APPLE PAY | Initializing Apple Pay session');
    this.appleSession = new ApplePaySession(14, paymentRequest);
    if(!this.appleSession) {
      return;
    }
    this.appleSession.onvalidatemerchant = async (
      event: ApplePayJS.ApplePayValidateMerchantEvent,
    ) => {
      // Implement merchant validation here
      this.Logger.trace('Validating Apple Pay merchant', event);

      try {
        const merchantValidationPayload = {
          request: 'getApplePaySession',
          url: event.validationURL,
          domain: window.location.hostname,
          merchantIdentifier: PAYWAY_MERCHANT_ID,
          paywayRequestToken: paywayRequestToken,
        };
        const reqBody = JSON.stringify(merchantValidationPayload);
        this.Logger.trace(
          'APPLE PAY |  Apple Pay validating merchant req body',
          reqBody,
        );

        const response = await fetch(this.serviceURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: reqBody,
        });
        this.Logger.trace(
          'APPLE PAY | Merchant validation response received',
          response,
        );
        if (!response.ok) {
          const text = await response.text();
          //this.errorHandler('Payway','accessToken fail');
          this.Logger.error(
            `APPLE PAY ERROR | Merchant validation error: ${response.status} ${text}`,
          );
          return;
        }
        let responseData = await response.text();
        const parsedResponseData = JSON.parse(responseData);
        this.Logger.trace(
          'APPLE PAY | Parsed merchant validation response data',
          parsedResponseData,
        );
        try {
          this.appleSession!.completeMerchantValidation(
            JSON.parse(parsedResponseData.appleSessionToken),
          );
        } catch (err) {
          //this.errorHandler('apple','completeMerchantValidation fail');
          this.Logger.error(
            'APPLE PAY ERROR | Cannot init complete merch validation ',
            err,
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.Logger.error(`APPLE PAY ERROR | Apple Validation reply error: ${message}`);
      }
    };

    this.appleSession.onpaymentauthorized = async (
      event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    ) => {
      // Implement payment authorization handling here
      this.Logger.trace('Apple Pay payment authorized', event);
      this.applePayPayment = event.payment;
      await PaywayService.Instance.processApplePayPayment();
    };

    this.appleSession.oncancel = (event: ApplePayJS.Event) => {
      this.Logger.trace('Apple Pay session cancelled', event);
    };

    try {
      const capabilities =
        await ApplePaySession.applePayCapabilities(PAYWAY_MERCHANT_ID);
      this.Logger.trace(
        'APPLE PAY | applePayCapabilities result:',
        capabilities,
      );
    } catch (e) {
      this.Logger.error('APPLE PAY | applePayCapabilities threw:', e);
    }

    try {
      this.appleSession.begin();
    } catch (error) {
      //this.errorHandler('Apple', 'start fail', false);
      this.Logger.error(
        'APPLE PAY ERROR | Apple Pay session failed to start:',
        error,
      );
    }
  }

  private getSourceId(): number {
    // Implementation for generating or retrieving the source ID for the Payway session
    return window.apple_config?.sourceId
      ? parseInt(window.apple_config.sourceId, 10)
      : 0;
  }

  public async processApplePayPayment(): Promise<void> {
    if(!this.applePayPayment || ! this.queuePaywayTokenResponse) {
      return;
    }
    try {
      // Implementation for processing the Apple Pay payment
      const authState = await Auth0Service.Instance.getAuthState();

      const paywayRequestJson: PaywayBillingRequest = {
        accountInputMode: 'applePay',
        paywayRequestToken: this.queuePaywayTokenResponse.paywayRequestToken,
        transactionName: this.queuePaywayTokenResponse.transactionName,
        transactionSourceId: window.apple_config!.sourceId
          ? parseInt(window.apple_config!.sourceId, 10)
          : 0,
        request: 'sendQueuedTransaction',
        applePayToken: this.applePayPayment.token.paymentData,
        merchantIdentifier: 'merchant.com.payway.mng',
        cardAccount: {
          firstName: this.applePayPayment.billingContact?.givenName || '',
          lastName: this.applePayPayment.billingContact?.familyName || '',
          address:
            this.applePayPayment.billingContact?.addressLines?.join(' ') || '',
          city: this.applePayPayment.billingContact?.locality || '',
          state: this.applePayPayment.billingContact?.administrativeArea || '',
          zip: this.applePayPayment.billingContact?.postalCode || '',
          email: authState.AuthProfile?.email || '', //for now we don't set this since this is the logged out flow and we don't have email yet
          phone: this.applePayPayment.billingContact?.phoneNumber || '',
        },
      };

      this.localStorageManger.set(
        STORAGE.applePayBillingRequestKey,
        paywayRequestJson,
      );
      
      const response = await fetch(this.billingURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paywayRequestJson),
      });

      const responseText = await response.text();
      this.Logger.trace('APPLE PAY | Response from Payway', responseText);

      if (response.ok) {
        this.appleSession!.completePayment(ApplePaySession.STATUS_SUCCESS);
        if( this.twoStepPaywall ) {
          hideElement(appleButtonsId)
        } else {
          hideElement(applePayButtonId)
        }
					
        this.addLoadMessage();				
        const res = await Auth0Service.Instance.getAuthState();
        if (!res.isAuthenticated) {
          const context = PipelineModule.getContext();
          this.Logger.trace('APPLE PAY | User is not authenticated, context: ', context);
          context.isAuthFromPayway = true;
          PipelineModule.updateContext(context);
          ScreenManager.Instance.showScreen('login');
          return;
        }
        this.processNewSubscriptionStart();
      } else {
        this.errorHandler('Payway', 'payway session fail');
        this.appleSession!.completePayment(ApplePaySession.STATUS_FAILURE);
      }
    } catch (err) {
      this.Logger.error('APPLE PAY ERROR | Naviga failed category ', err);
      const createMessage = document.querySelector('#creatingSubtext');
      if (createMessage) {
        createMessage.remove();
      }
    }
  };

  public async processNewSubscriptionStart(): Promise<void> {
    try{
      // Implementation for processing the new subscription start
      this.Logger.trace('APPLE PAY | Starting new subscription process');
      const authState = await Auth0Service.Instance.getAuthState();

      if (!authState || !authState.isAuthenticated) {
        this.Logger.trace(
          'APPLE PAY | User is not authenticated. Aborting new subscription start.',
        );
        return Promise.resolve();
      }

      let newSubStartBody: NewSubscriptionStartBody;

      if (this.hasPendingPaymentAuthorization()) {
        this.Logger.trace('APPLE PAY | User has a pending payment authorization');
        newSubStartBody =
          this.localStorageManger.getParsed<NewSubscriptionStartBody>(
            STORAGE.applePayNewStartKey,
          );
        this.Logger.trace(
          'APPLE PAY | Retrieved new subscription start body from local storage',
          newSubStartBody,
        );
      } else {
        if(!this.queuePaywayTokenResponse || !this.applePayPayment ) {
          this.Logger.trace('APPLE PAY | Failure to process new sub, missing tokens');
          this.errorHandler('Digisubs', 'subscription request fail');
          return;
        }
        newSubStartBody = {
          paywayRequestToken: this.queuePaywayTokenResponse.paywayRequestToken,
          applePayData: this.applePayPayment,
          firstName: this.applePayPayment.billingContact?.givenName || '',
          lastName: this.applePayPayment.billingContact?.familyName || '',
          address: this.applePayPayment.billingContact?.addressLines?.join(' ') || '',
          city: this.applePayPayment.billingContact?.locality || '',
          state: this.applePayPayment.billingContact?.administrativeArea || '',
          zip: this.applePayPayment.billingContact?.postalCode || '',
          email: authState.AuthProfile?.email || '',
          offerId: this.offerId,
          offerGroupId: this.offerGroupId,
          uuid: authState.AuthProfile?.auth0Id || '',
          domain: OptionsModule.Options.domain,
          paymentAmount: this.offerDetails!.promo.amount,
          sourceId: this.sourceId!,
          phone: this.applePayPayment.billingContact?.phoneNumber || '',
        };
      }

      //we don't have a pending meaning this isn't a continuation, set localstorage object.
      if (!this.hasPendingPaymentAuthorization()) {
        this.Logger.trace(
          'APPLE PAY | Setting new subscription start body in local storage no pending payment authorization',
          newSubStartBody,
        );
        this.localStorageManger.set(
          STORAGE.applePayNewStartKey,
          JSON.stringify(newSubStartBody),
        );
      }

      this.Logger.trace(
        'APPLE PAY | sending new subscription start request to entitler',
        JSON.stringify(newSubStartBody),
      );

      const entitlerEndpoint = `${SETTINGS.entitler.baseUrl}payway/create_subscription`;
      const subscriptionResult = await fetch(entitlerEndpoint, {
        method: 'POST',
        headers: {
          'X-Api-Key': SETTINGS.entitler.apiKey,
        },
        mode: 'cors',
        body: JSON.stringify(newSubStartBody),
      });
      this.Logger.trace('APPLE PAY | response from entitler', subscriptionResult);
      const data = await subscriptionResult.json();
      this.Logger.trace('APPLE PAY | response data from entitler', data);
      if (!subscriptionResult.ok) {
        this.Logger.trace('APPLE PAY | response from entitler NOT OK!!');
        this.errorHandler('Digisubs', 'subscription request fail');
        const createMessage = document.querySelector('#creatingSubtext');
        if (createMessage) {
          createMessage.remove();
        }
        return;
      }

      window.MG2DL?.push({
        event: 'g2i_subscription_success',
        payment_type: this.offerDetails?.regularBilling?.paymentTiming,
        sp_offerPlanName: 'apple_pay',
      });
      this.Logger.trace(
        'APPLE PAY | response from entitler and refreshing token ',
      );
      this.localStorageManger.delete(STORAGE.applePayNewStartKey);
      this.localStorageManger.delete(STORAGE.applePayBillingRequestKey);
      await PipelineModule.startUserInitiatedAuthPipeline(new ClearEntitlementCacheStep())
      window.Connext.CloseTemplates();
			window.location.reload();
      return Promise.resolve();
  } catch(err) {
    this.Logger.trace('APPLE PAY | error in sub start ', err)
  }
  };

  private	addLoadMessage() {
		const navigaModalBody = document.querySelector('#adhoc__newbody');
		if (!navigaModalBody) return;
		const awaitSubDiv = document.createElement('div');
		awaitSubDiv.id = 'creatingSubtext';
		awaitSubDiv.textContent = 'Creating Your Subscription...';
		awaitSubDiv.style.fontWeight = '700'; // bold
		awaitSubDiv.style.fontSize = '20px';
		awaitSubDiv.style.padding = '1rem 0';
		awaitSubDiv.style.color = 'black';
		const termsText = navigaModalBody.querySelector('p.terms');
		if (termsText) {
			termsText.insertAdjacentElement('afterend', awaitSubDiv);
		} else {
			navigaModalBody.appendChild(awaitSubDiv);
		} 
	}

  private errorHandler(module: string, message: string): void {
    this.Logger.error(`[${module}] ${message}`);
    showElement('.error');
  }
}
